import { Table } from 'antd';
import { useMemo, useState } from 'react';
import ReactDragListView from 'react-drag-listview';
import { Resizable } from 'react-resizable';

import 'react-resizable/css/styles.css';
import './resizableTable.less';

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;
  if (!width) return <th {...restProps} />;
  return (
    <Resizable
      width={width}
      height={0}
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
      handle={<span className="react-resizable-handle" onClick={(e) => e.stopPropagation()} />}
    >
      <th {...restProps} />
    </Resizable>
  );
};

/**
 * antd Table-ийг өргөн өөрчлөх (resize) болон drag хийж байрлал солих
 * боломжтой баганатай болгож өгдөг wrapper. Багануудын өргөн/эрэмбэ
 * зөвхөн component-ийн дотоод state-д хадгалагдана (refresh хийвэл арилна).
 *
 * `lockFirstColumn` (default true): эхний багана (жиш нь дугаарлалт) байрандаа
 * түгжигдэнэ. `fixed: "left"/"right"` бүхий багана (жиш нь мөрийн үйлдлийн
 * товч) байрлалаас үл хамааран үргэлж түгжигдсэн байна — resize/drag-д
 * оролцохгүй.
 *
 * `onColumnOrderChange` өгвөл эрэмбийг гаднаас удирдана (жиш нь
 * useColumnSettings): drag хийхэд дотоод state-д хадгалахгүй, түгжээгүй
 * баганын шинэ key-үүдийн дарааллыг энэ callback-аар буцаана.
 */
const PPResizableTable = ({
  columns,
  lockFirstColumn = true,
  onColumnOrderChange,
  ...tableProps
}) => {
  const [columnLayout, setColumnLayout] = useState(null);

  const isLocked = (col, index) => (lockFirstColumn && index === 0) || Boolean(col.fixed);

  const orderedColumns = useMemo(() => {
    let draggable = columns
      .filter((col, index) => !isLocked(col, index))
      .map((col) => ({
        ...col,
        width: columnLayout?.widths?.[col.key] ?? col.width,
      }));

    if (columnLayout?.order) {
      const orderIndex = new Map(columnLayout.order.map((key, i) => [key, i]));
      draggable = [...draggable].sort(
        (a, b) => (orderIndex.get(a.key) ?? 999) - (orderIndex.get(b.key) ?? 999)
      );
    }

    let di = 0;
    return columns.map((col, index) => (isLocked(col, index) ? col : draggable[di++]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, columnLayout, lockFirstColumn]);

  const handleResize =
    (col) =>
    (_e, { size }) => {
      // Эрэмбийг энд царцаахгүй — гаднаас удирдаж буй эрэмбийг дарахгүйн тулд
      setColumnLayout((prev) => ({
        order: prev?.order ?? null,
        widths: { ...(prev?.widths ?? {}), [col.key]: size.width },
      }));
    };

  // rowSelection идэвхтэй бол antd эхэнд checkbox-ийн нэмэлт th нэмдэг тул
  // drag хийхэд ирэх индексүүд манай columns массивтай харьцуулахад +1 шилждэг.
  const rowSelectionOffset = tableProps.rowSelection ? 1 : 0;

  const handleDragEnd = (domFromIndex, domToIndex) => {
    const fromIndex = domFromIndex - rowSelectionOffset;
    const toIndex = domToIndex - rowSelectionOffset;

    const draggablePositions = orderedColumns
      .map((col, index) => ({ col, index }))
      .filter(({ col, index }) => !isLocked(col, index));

    const from = draggablePositions.findIndex((p) => p.index === fromIndex);
    const to = draggablePositions.findIndex((p) => p.index === toIndex);
    if (from === -1 || to === -1 || from === to) return;

    if (onColumnOrderChange) {
      const nextOrder = draggablePositions.map((p) => p.col.key);
      const [moved] = nextOrder.splice(from, 1);
      nextOrder.splice(to, 0, moved);
      onColumnOrderChange(nextOrder);
      return;
    }

    setColumnLayout((prev) => {
      const order = prev?.order ?? draggablePositions.map((p) => p.col.key);
      const nextOrder = [...order];
      const [moved] = nextOrder.splice(from, 1);
      nextOrder.splice(to, 0, moved);
      return { order: nextOrder, widths: prev?.widths ?? {} };
    });
  };

  const renderColumns = orderedColumns.map((col, index) =>
    isLocked(col, index)
      ? col
      : {
          ...col,
          ellipsis: col.ellipsis ?? true,
          title:
            typeof col.title === 'string' ? (
              <span className="pp-drag-handle">{col.title}</span>
            ) : (
              col.title
            ),
          onHeaderCell: (column) => ({
            width: column.width,
            onResize: handleResize(column),
          }),
        }
  );

  return (
    <ReactDragListView.DragColumn
      nodeSelector="th"
      handleSelector=".pp-drag-handle"
      ignoreSelector=".react-resizable-handle"
      onDragEnd={handleDragEnd}
    >
      <Table
        {...tableProps}
        columns={renderColumns}
        components={{ header: { cell: ResizableTitle } }}
      />
    </ReactDragListView.DragColumn>
  );
};

export default PPResizableTable;
